<template>
  <div class="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-12">
    <div class="mb-8 mt-4">
      <h1 class="text-3xl font-bold text-slate-100">Meu Perfil</h1>
      <p class="text-slate-400 mt-2">Atualize suas informações pessoais</p>
    </div>

    <div class="rounded-xl border border-cyan-400/30 bg-slate-900/80 backdrop-blur-sm shadow-xl shadow-black/30 p-6">
      <form @submit.prevent="saveProfile" class="space-y-6">
        <div>
          <ImageCropUpload
            v-model="form.profile_image"
            upload-strategy="user_me"
            :max-file-size="PROFILE_IMAGE_MAX_BYTES"
            :name="`${form.first_name} ${form.last_name}`"
            label-under-photo-color-class="text-slate-200"
            @upload-success="handlePhotoUploadSuccess"
            @upload-error="handlePhotoUploadError"
          />
        </div>

        <div>
          <h2 class="text-lg font-semibold text-slate-100 mb-4">Informações Pessoais</h2>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-1">Nome *</label>
              <input
                v-model="form.first_name"
                type="text"
                required
                class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-slate-300 mb-1">Sobrenome *</label>
              <input
                v-model="form.last_name"
                type="text"
                required
                class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70"
              />
            </div>
          </div>
        </div>

        <div>
          <h2 class="text-lg font-semibold text-slate-100 mb-4">Informações de Contato</h2>
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-1">E-mail *</label>
              <input
                v-model="form.email"
                type="email"
                required
                class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-slate-300 mb-1">Telefone</label>
              <input
                v-model="form.phone"
                @input="applyPhoneMask"
                type="tel"
                placeholder="(00) 00000-0000"
                maxlength="15"
                class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70"
              />
            </div>
          </div>
        </div>

        <div>
          <h2 class="text-lg font-semibold text-slate-100 mb-4">Alterar Senha</h2>
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-1">Senha Atual</label>
              <div class="relative">
                <input
                  v-model="form.current_password"
                  :type="showCurrentPassword ? 'text' : 'password'"
                  autocomplete="current-password"
                  class="w-full pl-3 pr-11 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70"
                />
                <button
                  type="button"
                  class="absolute inset-y-0 right-0 flex items-center justify-center w-11 text-slate-400 hover:text-[#2AD7DE] focus:outline-none focus-visible:text-[#2AD7DE] rounded-r-lg"
                  :aria-label="showCurrentPassword ? 'Ocultar senha atual' : 'Mostrar senha atual'"
                  :aria-pressed="showCurrentPassword"
                  @click="showCurrentPassword = !showCurrentPassword"
                >
                  <svg v-if="!showCurrentPassword" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                </button>
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium text-slate-300 mb-1">Nova Senha</label>
              <div class="relative">
                <input
                  v-model="form.password"
                  :type="showNewPassword ? 'text' : 'password'"
                  autocomplete="new-password"
                  class="w-full pl-3 pr-11 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70"
                />
                <button
                  type="button"
                  class="absolute inset-y-0 right-0 flex items-center justify-center w-11 text-slate-400 hover:text-[#2AD7DE] focus:outline-none focus-visible:text-[#2AD7DE] rounded-r-lg"
                  :aria-label="showNewPassword ? 'Ocultar nova senha' : 'Mostrar nova senha'"
                  :aria-pressed="showNewPassword"
                  @click="showNewPassword = !showNewPassword"
                >
                  <svg v-if="!showNewPassword" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                </button>
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium text-slate-300 mb-1">Confirmar Nova Senha</label>
              <div class="relative">
                <input
                  v-model="form.password_confirmation"
                  :type="showConfirmPassword ? 'text' : 'password'"
                  autocomplete="new-password"
                  class="w-full pl-3 pr-11 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70"
                />
                <button
                  type="button"
                  class="absolute inset-y-0 right-0 flex items-center justify-center w-11 text-slate-400 hover:text-[#2AD7DE] focus:outline-none focus-visible:text-[#2AD7DE] rounded-r-lg"
                  :aria-label="showConfirmPassword ? 'Ocultar confirmação de senha' : 'Mostrar confirmação de senha'"
                  :aria-pressed="showConfirmPassword"
                  @click="showConfirmPassword = !showConfirmPassword"
                >
                  <svg v-if="!showConfirmPassword" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="successMessage"
          class="bg-emerald-500/10 border border-emerald-400/35 text-emerald-200 px-4 py-3 rounded-lg flex items-center space-x-2"
        >
          <svg class="w-5 h-5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
          </svg>
          <span>{{ successMessage }}</span>
        </div>

        <div
          v-if="errors.general"
          class="bg-red-500/10 border border-red-400/40 text-red-300 px-4 py-3 rounded-lg flex items-center space-x-2"
        >
          <svg class="w-5 h-5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
          </svg>
          <span>{{ errors.general }}</span>
        </div>

        <div class="flex justify-end space-x-3 pt-6 border-t border-slate-700/70">
          <button
            type="submit"
            :disabled="saving"
            class="px-6 py-2 rounded-lg bg-[#2AD7DE] hover:bg-[#5de9f0] text-slate-950 font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ saving ? 'Salvando...' : 'Salvar Alterações' }}
          </button>
          <button
            type="button"
            @click="goBack"
            class="px-6 py-2 rounded-lg border border-slate-500/80 text-slate-200 hover:bg-slate-800/60 transition font-medium"
          >
            Cancelar
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { navigateTo } from '../router.js'
import { useApi } from '../composables/useApi.js'
import { useInputMasks } from '../composables/useInputMasks.js'
import ImageCropUpload from './ImageCropUpload.vue'

/** Alinhado a `User` — `validates_blob_byte_size profile_image: 1.megabytes` */
const PROFILE_IMAGE_MAX_BYTES = 1024 * 1024

const { applyPhoneMask } = useInputMasks()

export default {
  components: {
    ImageCropUpload
  },
  emits: ['user-updated'],
  props: {
    user: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      showCurrentPassword: false,
      showNewPassword: false,
      showConfirmPassword: false,
      PROFILE_IMAGE_MAX_BYTES,
      form: {
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        profile_image: '',
        current_password: '',
        password: '',
        password_confirmation: ''
      },
      saving: false,
      successMessage: '',
      errors: {},
      stateOptions: []
    }
  },

  mounted() {
    this.loadUserData()
    this.fetchProfile()
  },

  methods: {
    loadUserData() {
      this.form = {
        first_name: this.user.first_name || '',
        last_name: this.user.last_name || '',
        email: this.user.email || '',
        phone: this.user.phone || '',
        profile_image: this.user.profile_image || '',
        current_password: '',
        password: '',
        password_confirmation: ''
      }
    },

    async fetchProfile() {
      try {
        const { get } = useApi()
        const { data, error } = await get('/api/v1/me')
        
        if (error) return
        
        const userData = data
        this.form = {
          first_name: userData.first_name || '',
          last_name: userData.last_name || '',
          email: userData.email || '',
          phone: userData.phone || '',
          profile_image: userData.profile_image || '',
          current_password: '',
          password: '',
          password_confirmation: ''
        }

        localStorage.setItem('user', JSON.stringify(userData))
      } catch (error) {
        console.error('Erro ao carregar perfil', error)
      }
    },

    async saveProfile() {
      this.saving = true
      this.errors = {}
      this.successMessage = ''

      try {
        const { put, upload } = useApi()
        const formData = new FormData()

        // Adicionar dados do usuário
        formData.append('user[first_name]', this.form.first_name)
        formData.append('user[last_name]', this.form.last_name)
        formData.append('user[email]', this.form.email)
        formData.append('user[phone]', this.form.phone)

        // Adicionar senhas se fornecidas
        if (this.form.current_password) {
          formData.append('user[current_password]', this.form.current_password)
        }
        if (this.form.password) {
          formData.append('user[password]', this.form.password)
        }
        if (this.form.password_confirmation) {
          formData.append('user[password_confirmation]', this.form.password_confirmation)
        }

        // Converter Data URL para Blob e adicionar à foto
        if (this.form.profile_image && this.form.profile_image.startsWith('data:')) {
          console.log('Convertendo Data URL para Blob...')
          const blob = await this.dataUrlToBlob(this.form.profile_image)
          console.log('Blob criado com sucesso:', blob)
          formData.append('user[profile_image]', blob, 'profile.jpg')
          console.log('Blob adicionado ao FormData')
        } else if (this.form.profile_image) {
          console.log('Foto não é Data URL (pode ser URL HTTP já existente):', this.form.profile_image.substring(0, 50))
        }

        console.log('Enviando PATCH para /api/v1/me com FormData')
        const { data, error } = await upload('/api/v1/me', formData)

        if (error) {
          console.error('Erro ao salvar perfil:', error)
          const errorList = error.response?.data?.errors
          if (Array.isArray(errorList)) {
            this.errors.general = errorList.join(', ')
          } else if (typeof errorList === 'object') {
            const firstKey = Object.keys(errorList)[0]
            this.errors.general = errorList[firstKey]?.[0] || 'Erro ao salvar perfil'
          } else {
            this.errors.general = errorList || 'Erro ao salvar perfil'
          }
          return
        }

        console.log('Resposta do servidor:', data)
        localStorage.setItem('user', JSON.stringify(data))
        this.form = {
          first_name: data.first_name || '',
          last_name: data.last_name || '',
          email: data.email || '',
          phone: data.phone || '',
          profile_image: data.profile_image || '',
          current_password: '',
          password: '',
          password_confirmation: ''
        }
        this.showCurrentPassword = false
        this.showNewPassword = false
        this.showConfirmPassword = false
        this.$emit('user-updated', data)

        this.successMessage = 'Perfil atualizado com sucesso!'
        setTimeout(() => {
          this.successMessage = ''
        }, 3000)
      } catch (err) {
        this.errors.general = 'Erro ao salvar perfil'
        console.error('Erro ao salvar perfil:', err)
      } finally {
        this.saving = false
      }
    },

    async dataUrlToBlob(dataUrl) {
      const response = await fetch(dataUrl)
      return await response.blob()
    },

    handlePhotoUploadSuccess(payload) {
      if (payload.user) {
        this.form.profile_image = payload.user.profile_image || payload.url || ''
        this.$emit('user-updated', payload.user)
      }
    },

    handlePhotoUploadError(error) {
      this.errors.photo = 'Erro ao processar foto de perfil'
      console.error('Erro ao fazer upload da foto:', error)
    },

    applyPhoneMask(e) {
      this.form.phone = applyPhoneMask(e.target.value)
    },

    goBack() {
      navigateTo('agent-dashboard')
    }
  }
}
</script>
